import { QueryResult, Unmasked, UseSuspenseQueryResult } from '@apollo/client';
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

export interface PaginatedVariables {
  start?: string
}

export function mergePaginatedList<
TData extends Record<string, any>,
TKey extends keyof TData,
>(existing: TData | undefined, incoming: TData | undefined, key: TKey) {
  return {
    ...incoming,
    [key]: [...(existing?.[key] || []), ...(incoming?.[key] || [])],
  } as TData;
}

export function usePaginator<
TData extends Record<string, any> | undefined,
TVariables extends PaginatedVariables = PaginatedVariables,
>(
  query: QueryResult<TData, TVariables> | UseSuspenseQueryResult<TData, TVariables>,
  getLastEvaluated: (data: TData) => string | undefined,
  merge: (existing: Unmasked<TData>, incoming: Unmasked<TData>) => Unmasked<TData>,
): [() => Promise<void>, boolean] {
  const { data, fetchMore } = query;

  const stableMerge = useRef(merge);
  stableMerge.current = merge;

  const lastEvaluated = data ? getLastEvaluated(data) : undefined;

  const fetchNextPage = useCallback(async () => {
    if (lastEvaluated) {
      return new Promise<void>((resolve) => {
        startTransition(async () => {
          await fetchMore({
            updateQuery: (existing, { fetchMoreResult }) => (
              stableMerge.current(existing, fetchMoreResult)
            ),
            variables: { start: lastEvaluated } as TVariables,
          });
          resolve();
        });
      });
    }
    return Promise.resolve(undefined);
  }, [fetchMore, lastEvaluated]);

  return useMemo(() => ([
    fetchNextPage,
    Boolean(lastEvaluated),
  ]), [fetchNextPage, lastEvaluated]);
}

/**
 * To handle pagination manually.
 * This hook will only fetch the first page and fetchMore must be called to fetch the next pages.
 */
export function useManualPagination<
TData extends Record<string, any> | undefined,
TVariables extends PaginatedVariables = PaginatedVariables,
>(
  query: QueryResult<TData, TVariables>,
  getLastEvaluated: (data: TData) => string | undefined,
  merge: (existing: Unmasked<TData>, incoming: Unmasked<TData>) => Unmasked<TData>,
) {
  const [fetchMore, hasNextPage] = usePaginator(query, getLastEvaluated, merge);

  return useMemo(() => ({
    ...query,
    fetchMore,
    hasNextPage,
  }), [fetchMore, hasNextPage, query]);
}

/**
 * To handle pagination manually with suspense.
 * This hook will only fetch the first page and fetchMore must be called to fetch the next pages.
 */
export function useManualSuspensePagination<
TData extends Record<string, any> | undefined,
TVariables extends PaginatedVariables = PaginatedVariables,
>(
  query: UseSuspenseQueryResult<TData, TVariables>,
  getLastEvaluated: (data: TData) => string | undefined,
  merge: (existing: Unmasked<TData>, incoming: Unmasked<TData>) => Unmasked<TData>,
) {
  const [fetchMore, hasNextPage] = usePaginator(query, getLastEvaluated, merge);

  return useMemo(() => ({
    ...query,
    fetchMore,
    hasNextPage,
  }), [fetchMore, hasNextPage, query]);
}

function useAutoFetchNextPage(fetchMore: () => Promise<void>, hasNextPage: boolean) {
  useEffect(() => {
    if (hasNextPage) {
      fetchMore();
    }
  }, [fetchMore, hasNextPage]);
}

/**
 * To handle pagination automatically.
 * This hook will fetch pages until lastEvaluated is empty.
 */
export function usePagination<
TData extends Record<string, any> | undefined,
TVariables extends PaginatedVariables = PaginatedVariables,
>(
  query: QueryResult<TData, TVariables>,
  getLastEvaluated: (data: TData) => string | undefined,
  merge: (existing: Unmasked<TData>, incoming: Unmasked<TData>) => Unmasked<TData>,
) {
  const paginatedQuery = useManualPagination(query, getLastEvaluated, merge);
  const { fetchMore, hasNextPage } = paginatedQuery;

  useAutoFetchNextPage(fetchMore, hasNextPage);

  return paginatedQuery;
}

/**
 * To handle pagination automatically with suspense.
 * This hook will fetch pages until lastEvaluated is empty.
 */
export function useSuspensePagination<
TData extends Record<string, any> | undefined,
TVariables extends PaginatedVariables = PaginatedVariables,
>(
  query: UseSuspenseQueryResult<TData, TVariables>,
  getLastEvaluated: (data: TData) => string | undefined,
  merge: (existing: Unmasked<TData>, incoming: Unmasked<TData>) => Unmasked<TData>,
) {
  const paginatedQuery = useManualSuspensePagination(query, getLastEvaluated, merge);
  const { fetchMore, hasNextPage } = paginatedQuery;

  useAutoFetchNextPage(fetchMore, hasNextPage);

  return paginatedQuery;
}
